import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Grid,
  Paper,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { blue } from "@mui/material/colors";
import { SCUIButton } from "../../../../atomic/atoms";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Widgets,
} from "@mui/icons-material";
import "../../CampaignManagement.scss";
import { StepStackableServiceComponent } from "../../services";
import { ICampaignMatrix } from "../../types/interfaces/CampaingTypes";
import { useEffect, useState } from "react";
import {
  CampaignStatuses,
  MatrixCampaignType,
  MatrixCampaignTypes,
} from "../../../../types/enums/campaign-enums";
import { SCUILiveCampaignFilter } from "../SCUILiveCampaignFilter/SCUILiveCampaignFilter";
import { useSelector } from "react-redux";

const toggleIdInSelection = (prevSelected: number[], id: number): number[] =>
  prevSelected.includes(id)
    ? prevSelected.filter((selectedId) => selectedId !== id)
    : [...prevSelected, id];

export const SCUICampaignStackableStep = ({ ...props }) => {
  const { t } = useTranslation();

  const liveCampaignStore = useSelector((state: any) => state.campaignLiveCampaign);

  const {
    saveCampaignForm,
    matrixes,
    dontMergeList,
    mergedList,
    dontApplySameProductList,
    stepperOnClickReset,
    moveCampaign,
    saveForm,
  } = StepStackableServiceComponent({ ...props });

  const Item = styled(Paper)(({ theme }) => ({
    ...theme.typography.body2,
    backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
    padding: theme.spacing(1),
    textAlign: "center",
    color: theme.palette.text.secondary,
  }));

  const DontMerge = ({ list }: any) => {
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const isCheckedItem = (id: number) => selectedIds.includes(id);

    const toggleSelection = (id: number) => {
      setSelectedIds((prevSelected) => toggleIdInSelection(prevSelected, id));
    };

    const toggleAllSelection = (event: any) => {
      const { checked } = event.target;
      const liveCampaignStatus = liveCampaignStore.data;

      let ids: number[] = [];

      if (checked) {
        ids = matrixes
          .filter((x: any) => x.isCombinable == MatrixCampaignType.DontMerge && (liveCampaignStatus === -1 ? x.status !== -1 : x.status === liveCampaignStore.data))
          .map((x: any) => x.campaignRuleId);
      }

      setSelectedIds(ids);
      setAllSelected(checked);
    };

    const reset = () => {
      setAllSelected(false);
      setSelectedIds([]);
    };

    useEffect(() => {
      if (list.length > 0) {
        setAllSelected(list.length == selectedIds.length);
      }
    }, [selectedIds]);

    return (
      <Card style={{ border: "1px solid #ccc" }}>
        <CardContent style={{ padding: "40px" }}>
          <Tooltip
            title={t(MatrixCampaignTypes.DontMerge.title)}
            placement="bottom-start"
          >
            <Typography
              component="h2"
              sx={{
                marginBottom: "10px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "bold",
                fontSize: { xs: "16px", md: "20px", lg: "24px" },
              }}
            >
              <>{t(MatrixCampaignTypes.DontMerge.title)}</>
            </Typography>
          </Tooltip>

          <Tooltip title={t(MatrixCampaignTypes.DontMerge.description)}>
            <Typography
              color="text.secondary"
              gutterBottom
              sx={{
                fontSize: "14px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t(MatrixCampaignTypes.DontMerge.description)}
            </Typography>
          </Tooltip>
          <TableContainer component={Paper} style={{ marginTop: "16px" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="6%">
                    <Checkbox
                      checked={allSelected}
                      onChange={(event) => {
                        toggleAllSelection(event);
                      }}
                    />
                  </TableCell>
                  <TableCell align="left" width="50%">
                    {t("campaignManagement.save.step.stackable.dontMerge.table.header.campaignName")}
                  </TableCell>
                  <TableCell align="left">{t("campaignManagement.save.step.stackable.dontMerge.table.header.status")}</TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <Box sx={{ height: "280px", overflowY: "auto" }}>
              <Table size="small">
                <TableBody>
                  {list &&
                    list.map((row: ICampaignMatrix) => (
                      <TableRow
                        key={row.campaignRuleId}
                        sx={{
                          "td, th": { border: 0, height: "30px" },
                        }}
                        style={{ height: 33 }}
                      >
                        <TableCell component="th" scope="row" width="10%">
                          <Checkbox
                            checked={isCheckedItem(row.campaignRuleId)}
                            onChange={() => {
                              toggleSelection(row.campaignRuleId);
                            }}
                          />
                        </TableCell>
                        <TableCell align="left" width="50%">
                          {row.name}
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            label={
                              t(CampaignStatuses.find((s: any) => s.id == row.status)?.name ?? "")
                            }
                            variant="outlined"
                            style={{
                              backgroundColor: CampaignStatuses.find(
                                (s: any) => s.id == row.status
                              )?.color,
                              color: "white",
                            }}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </CardContent>
        <CardActions>
          <Stack
            style={{
              width: "100%",
              alignItems: "flex-start",
              padding: "10px 28px",
              height: "140px",
              justifyContent: "flex-end",
            }}
          >
            <Item>
              <SCUIButton
                text={t("campaignManagement.save.step.stackable.dontMerge.action.toMergeButton")}
                type="secondary"
                sizeType="icon"
                size={"s"}
                showEndIcon={true}
                endIcon={<KeyboardArrowRight />}
                onClick={() => {
                  moveCampaign(MatrixCampaignType.Merged, selectedIds);
                  reset();
                }}
              />
            </Item>
            <Item>
              <SCUIButton
                text={t("campaignManagement.save.step.stackable.dontMerge.action.toDontApplyToSameProductButton")}
                type="secondary"
                sizeType="fixed"
                size={"s"}
                showEndIcon={true}
                endIcon={<KeyboardArrowRight />}
                className="stackableCardAction"
                onClick={() => {
                  moveCampaign(
                    MatrixCampaignType.DontApplySameProducts,
                    selectedIds
                  );
                  reset();
                }}
              />
            </Item>
          </Stack>
        </CardActions>
      </Card>
    );
  };

  const Merged = ({ list }: any) => {
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const isCheckedItem = (id: number) => selectedIds.includes(id);

    const toggleSelection = (id: number) => {
      setSelectedIds((prevSelected) => toggleIdInSelection(prevSelected, id));
    };

    const toggleAllSelection = (event: any) => {
      const { checked } = event.target;
      const liveCampaignStatus = liveCampaignStore.data;

      let ids: number[] = [];

      if (checked) {
        ids = matrixes
          .filter((x: any) => x.isCombinable == MatrixCampaignType.Merged && (liveCampaignStatus === -1 ? x.status !== -1 : x.status === liveCampaignStore.data))
          .map((x: any) => x.campaignRuleId);
      }

      setSelectedIds(ids);
      setAllSelected(checked);
    };

    const reset = () => {
      setAllSelected(false);
      setSelectedIds([]);
    };

    useEffect(() => {
      if (list.length > 0) {
        setAllSelected(list.length == selectedIds.length);
      }
    }, [selectedIds]);

    return (
      <Card style={{ border: "1px solid #ccc" }}>
        <CardContent style={{ padding: "40px" }}>
          <Tooltip
            title={t(MatrixCampaignTypes.Merged.title)}
            placement="bottom-start"
          >
            <Typography
              component="h2"
              sx={{
                marginBottom: "10px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "bold",
                fontSize: { xs: "16px", md: "20px", lg: "24px" },
              }}
            >
              <>{t(MatrixCampaignTypes.Merged.title)}</>
            </Typography>
          </Tooltip>
          <Tooltip title={t(MatrixCampaignTypes.Merged.description)}>
            <Typography
              color="text.secondary"
              gutterBottom
              sx={{
                fontSize: "14px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t(MatrixCampaignTypes.Merged.description)}
            </Typography>
          </Tooltip>
          <TableContainer component={Paper} style={{ marginTop: "16px" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="6%">
                    <Checkbox
                      checked={allSelected}
                      onChange={(event) => {
                        toggleAllSelection(event);
                      }}
                    />
                  </TableCell>
                  <TableCell align="left" width="50%">
                    {t("campaignManagement.save.step.stackable.merged.table.header.campaignName")}
                  </TableCell>
                  <TableCell align="left">{t("campaignManagement.save.step.stackable.merged.table.header.status")}</TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <Box sx={{ height: "280px", overflowY: "auto" }}>
              <Table size="small">
                <TableBody>
                  {list &&
                    list.map((row: ICampaignMatrix) => (
                      <TableRow
                        key={row.campaignRuleId}
                        sx={{
                          "td, th": { border: 0, height: "30px" },
                        }}
                        style={{ height: 33 }}
                      >
                        <TableCell component="th" scope="row" width="10%">
                          <Checkbox
                            checked={isCheckedItem(row.campaignRuleId)}
                            onChange={() => {
                              toggleSelection(row.campaignRuleId);
                            }}
                          />
                        </TableCell>
                        <TableCell align="left" width="50%">
                          {row.name}
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            label={
                              t(CampaignStatuses.find((s: any) => s.id == row.status)?.name ?? "")
                            }
                            variant="outlined"
                            style={{
                              backgroundColor: CampaignStatuses.find(
                                (s: any) => s.id == row.status
                              )?.color,
                              color: "white",
                            }}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </CardContent>
        <CardActions>
          <Stack
            style={{
              width: "100%",
              alignItems: "flex-end",
              padding: "10px 28px",
              height: "140px",
              justifyContent: "flex-end",
            }}
          >
            <Item>
              <SCUIButton
                text={t("campaignManagement.save.step.stackable.merged.action.removeButton")}
                type="secondary"
                sizeType="icon"
                size={"s"}
                showStartIcon={true}
                startIcon={<KeyboardArrowLeft />}
                className="stackableCardAction"
                onClick={() => {
                  moveCampaign(MatrixCampaignType.DontMerge, selectedIds);
                  reset();
                }}
              />
            </Item>
          </Stack>
        </CardActions>
      </Card>
    );
  };

  const DontApplySameProducts = ({ list }: any) => {
    const [allSelected, setAllSelected] = useState<boolean>(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    const isCheckedItem = (id: number) => selectedIds.includes(id);

    const toggleSelection = (id: number) => {
      setSelectedIds((prevSelected) => toggleIdInSelection(prevSelected, id));
    };

    const toggleAllSelection = (event: any) => {
      const { checked } = event.target;
      const liveCampaignStatus = liveCampaignStore.data;

      let ids: number[] = [];

      if (checked) {
        ids = matrixes
          .filter((x: any) => x.isCombinable == MatrixCampaignType.DontApplySameProducts && (liveCampaignStatus === -1 ? x.status !== -1 : x.status === liveCampaignStore.data))
          .map((x: any) => x.campaignRuleId);
      }

      setSelectedIds(ids);
      setAllSelected(checked);
    };

    const reset = () => {
      setAllSelected(false);
      setSelectedIds([]);
    };

    useEffect(() => {
      if (list.length > 0) {
        setAllSelected(list.length == selectedIds.length);
      }
    }, [selectedIds]);

    return (
      <Card style={{ border: "1px solid #ccc" }}>
        <CardContent style={{ padding: "40px" }}>
          <Tooltip
            title={t(MatrixCampaignTypes.DontApplySameProducts.title)}
            placement="bottom-start"
          >
            <Typography
              component="h2"
              sx={{
                marginBottom: "10px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "bold",
                fontSize: { xs: "16px", md: "20px", lg: "24px" },
              }}
            >
              <>{t(MatrixCampaignTypes.DontApplySameProducts.title)}</>
            </Typography>
          </Tooltip>
          <Tooltip
            title={t(MatrixCampaignTypes.DontApplySameProducts.description)}
          >
            <Typography
              color="text.secondary"
              gutterBottom
              sx={{
                fontSize: "14px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {t(MatrixCampaignTypes.DontApplySameProducts.description)}
            </Typography>
          </Tooltip>
          <TableContainer component={Paper} style={{ marginTop: "16px" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="6%">
                    <Checkbox
                      checked={allSelected}
                      onChange={(event) => {
                        toggleAllSelection(event);
                      }}
                    />
                  </TableCell>
                  <TableCell align="left" width="50%">
                    {t("campaignManagement.save.step.stackable.dontApplyToSameProducts.table.header.campaignName")}
                  </TableCell>
                  <TableCell align="left">{t("campaignManagement.save.step.stackable.dontApplyToSameProducts.table.header.status")}</TableCell>
                </TableRow>
              </TableHead>
            </Table>
            <Box sx={{ height: "280px", overflowY: "auto" }}>
              <Table size="small">
                <TableBody>
                  {list &&
                    list.map((row: ICampaignMatrix) => (
                      <TableRow
                        key={row.campaignRuleId}
                        sx={{
                          "td, th": { border: 0, height: "30px" },
                        }}
                        style={{ height: 33 }}
                      >
                        <TableCell component="th" scope="row" width="10%">
                          <Checkbox
                            checked={isCheckedItem(row.campaignRuleId)}
                            onChange={() => {
                              toggleSelection(row.campaignRuleId);
                            }}
                          />
                        </TableCell>
                        <TableCell align="left" width="50%">
                          {row.name}
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            label={
                              t(CampaignStatuses.find((s: any) => s.id == row.status)?.name ?? "")
                            }
                            variant="outlined"
                            style={{
                              backgroundColor: CampaignStatuses.find(
                                (s: any) => s.id == row.status
                              )?.color,
                              color: "white",
                            }}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </TableContainer>
        </CardContent>
        <CardActions>
          <Stack
            style={{
              width: "100%",
              alignItems: "flex-end",
              padding: "10px 28px",
              height: "140px",
              justifyContent: "flex-end",
            }}
          >
            <Item>
              <SCUIButton
                text={t("campaignManagement.save.step.stackable.dontApplyToSameProducts.action.removeButton")}
                type="secondary"
                sizeType="icon"
                size={"s"}
                showStartIcon={true}
                startIcon={<KeyboardArrowLeft />}
                className="stackableCardAction"
                onClick={() => {
                  moveCampaign(MatrixCampaignType.DontMerge, selectedIds);
                  reset();
                }}
              />
            </Item>
          </Stack>
        </CardActions>
      </Card>
    );
  };

  return (
    <Card className="step-form-card">
      <form
        id="StepStackable"
        name="StepStackable"
        noValidate={false}
        // onSubmit={stepperOnClickNext}
      >
        <CardHeader
          className="step-form-card-header"
          avatar={
            <Avatar
              sx={{ bgcolor: blue[50], color: blue["A700"] }}
              variant="rounded"
            >
              <Widgets sx={{ height: 24, width: 24 }} />
            </Avatar>
          }
          title={t("campaignManagement.save.step.stackable.title")}
          subheader={t("campaignManagement.save.step.stackable.description")}
        />

        <CardContent className="step-form-card-content">

          <SCUILiveCampaignFilter />

          <Grid container>
            <Grid
              size={{
                xs: 4,
                sm: 4,
                md: 4,
                lg: 4
              }}>
              <DontMerge list={dontMergeList} />
            </Grid>
            <Grid
              size={{
                xs: 4,
                sm: 4,
                md: 4,
                lg: 4
              }}>
              <Merged list={mergedList} />
            </Grid>
            <Grid
              size={{
                xs: 4,
                sm: 4,
                md: 4,
                lg: 4
              }}>
              <DontApplySameProducts list={dontApplySameProductList} />
            </Grid>
          </Grid>
            
        </CardContent>

        <CardActions
          sx={{ m: 1 }}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Stack direction="row">
            <Item>
              <SCUIButton
                text={t(
                  "campaignManagement.save.step.action.backButton"
                )}
                type="secondary"
                sizeType="icon"
                size={"s"}
                buttonType="button"
                onClick={stepperOnClickReset}
              />
            </Item>
            <Item>
              <SCUIButton
                text={t(
                  "campaignManagement.save.step.action.saveButton"
                )}
                type="secondary"
                sizeType="fixed"
                size={"s"}
                buttonType="button"
                onClick={() => saveCampaignForm({})}
              />
            </Item>
            <Item>
              <SCUIButton
                text={t(
                  "campaignManagement.save.step.action.saveAndContinueButton"
                )}
                type="primary"
                sizeType="fixed"
                size={"s"}
                buttonType="button"
                onClick={saveForm}
              />
            </Item>
          </Stack>
        </CardActions>
      </form>
    </Card>
  );
};
